import _ from "lodash"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"

import Client from "data/_client"
import { ApiResult } from "data/interface/api"
import notificationService from "data/notification"
import { diff_obj } from 'data/lib/diff'
import { push_undo } from 'data/lib/undo'
import { handleReferences } from 'data/lib/handle-references'
import incrementUnique from 'data/lib/increment-unique'
import { alphanum } from "components/table/lib/sort"
import { PromiseWatch } from "./lib/promise-helpers"

export type RenewalProbability = {
  propertyid: number
  renewalprobabilityid: number
}
// private module logic //

const id: string = "renewalProbability"
const initialState: ApiResult<RenewalProbability[]> = { id, state: "init", value: [] }
const renewalProbabilityStream = new BehaviorSubject<ApiResult<RenewalProbability[]>>(initialState)
const renewalProbabilityEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})
let renewalProbabilityMap = {}
const _patches_ = PromiseWatch()
const _fetches_ = PromiseWatch()

function clear(): void {
  renewalProbabilityStream.next({ id, state: 'ok', value: [] })
  renewalProbabilityMap = {}
}

function get(renewalProbabilityMapId: number): RenewalProbability {
  return renewalProbabilityMap[renewalProbabilityMapId]
}

function refresh(): Promise<any> {
  const currentValue: RenewalProbability[] = renewalProbabilityStream.getValue().value
  const propertyid = _.get(currentValue, '0.propertyid')
  if (_.isNil(propertyid)) return Promise.resolve()
  return fetchRenewalProbability(propertyid)
}

function fetchRenewalProbability(
  propertyId: number,
  limit: number = 0,
  offset: number = 0
): Promise<RenewalProbability[] | { message: string }> {
  if (_fetches_.has(propertyId)) return _fetches_.first(propertyId)

  clear()
  renewalProbabilityStream.next({ id, state: 'busy', value: renewalProbabilityStream.getValue().value })

  let url = `renewalprobability?propertyid=eq.${propertyId}&order=type.asc,code.asc.nullsfirst`
  if (limit && offset >= 0) {
    url = `renewalprobability?propertyid=eq.${propertyId}&limit=${limit}&offset=${offset}&order=type.asc,code.asc.nullsfirst`
  }

  return _fetches_.watch(propertyId, Client.get(url)
  .then((records: RenewalProbability[]) => {
    _.forEach(records, (renewalProbability) => (renewalProbabilityMap[renewalProbability.renewalprobabilityid] = renewalProbability))
    // merge results unique by renewalprobabilityid
    renewalProbabilityStream.next({ id, state: 'ok', value: records })
    return records
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  }))
}

function create(propertyid: number): Promise<RenewalProbability | { message: string }> {
  const currentValue: RenewalProbability[] = renewalProbabilityStream.getValue().value
  renewalProbabilityStream.next({ id, state: 'busy', value: currentValue })

  const payload = {
    propertyid,
    inputtype: 'basic',
    renewalprobabilitydetail: {
      state: "basic"
    }
  }

  return Client.post(`renewalprobability`, payload)
    .then((renewalProbability: RenewalProbability) => {
      push_undo({
        undofunc: () => deleteOne(propertyid, renewalProbability.renewalprobabilityid),
        redofunc: () => create(propertyid)
      })
      renewalProbabilityMap[renewalProbability.renewalprobabilityid] = renewalProbability
      // merge results unique by renewalprobabilityid
      const currentValue: RenewalProbability[] = renewalProbabilityStream.getValue().value
      renewalProbabilityStream.next({
        id,
        state: 'ok',
        value: _.unionBy([renewalProbability], currentValue, 'renewalprobabilityid')
      })
      renewalProbabilityEventStream.next({ type: 'RENEWALPROBABILITY_CREATED', payload: renewalProbability })
      return renewalProbability
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      renewalProbabilityStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      fetchRenewalProbability(propertyid)
      return err
    })
}

function copy(propertyid:number, renewalprobabilityid:number, code:string) {
  const currentValue: RenewalProbability[] = renewalProbabilityStream.getValue().value;
  renewalProbabilityStream.next({ id, state: 'busy', value: currentValue });

  const payload = {table: 'renewalprobability', recordid: renewalprobabilityid, _synchronous: true, code: incrementUnique(code, "code", currentValue)};

  Client.post(`msg/svc_recordcopy`, payload)
    .then((renewalProbability: RenewalProbability) => {
      const newRecord = renewalProbability[0]
      push_undo({
        undofunc: () => deleteOne(propertyid, newRecord.renewalprobabilityid),
        redofunc: () => copy(propertyid, renewalprobabilityid, code)
      });
      renewalProbabilityMap[newRecord.renewalprobabilityid] = newRecord
      // merge results unique by renewalprobabilityid
      const currentValue: RenewalProbability[] = renewalProbabilityStream.getValue().value
      renewalProbabilityStream.next({
        id,
        state: 'ok',
        value: _.unionBy([newRecord], currentValue, 'renewalprobabilityid').sort((a, b) => alphanum(a.code, b.code))
      })
      renewalProbabilityEventStream.next({ type: 'RENEWALPROBABILITY_COPIED', payload: newRecord })
      return newRecord
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      renewalProbabilityStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchRenewalProbability(propertyid)
      return err
    })
}

function copies(propertyid: number, renewalprobabilityid: number, count: number) {
  const currentValue: RenewalProbability[] = renewalProbabilityStream.getValue().value;
  renewalProbabilityStream.next({ id, state: 'busy', value: currentValue });

  const payload = {table: 'renewalprobability', recordid: renewalprobabilityid, _synchronous: true, unique: true, count};

  return Client.post(`msg/svc_recordcopy`, payload)
    .then((renewalProbabilities: RenewalProbability[]) => {
      push_undo({
        undofunc: () => _.forEach(renewalProbabilities, newRecord => deleteOne(propertyid, newRecord.renewalprobabilityid)),
        redofunc: () => copies(propertyid, renewalprobabilityid, count)
      });
      _.forEach(renewalProbabilities, newRecord => renewalProbabilityMap[newRecord.renewalprobabilityid] = newRecord)
      // merge results unique by renewalprobabilityid
      const currentValue: RenewalProbability[] = renewalProbabilityStream.getValue().value
      renewalProbabilityStream.next({
        id,
        state: 'ok',
        value: _.unionBy(renewalProbabilities, currentValue, 'renewalprobabilityid').sort((a, b) => alphanum(a.code, b.code))
      })
      renewalProbabilityEventStream.next({ type: 'RENEWALPROBABILITY_COPIED', payload: renewalProbabilities })
      return renewalProbabilities
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      renewalProbabilityStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchRenewalProbability(propertyid)
      return err
    })
}

async function deleteOne(
  propertyid: number,
  renewalprobabilityid: number,
): Promise<boolean | { message: string }> {
  const currentValue: RenewalProbability[] = renewalProbabilityStream.getValue().value

  const deleteFn = () => deleteOne(propertyid, renewalprobabilityid)
  const refreshFn = () => fetchRenewalProbability(propertyid)
  if (!(await handleReferences('renewalprobability', renewalprobabilityid, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted renewalprobabilityid
  const value = _(currentValue)
    .filter((p) => p.renewalprobabilityid !== renewalprobabilityid)
    .value()
  value.forEach((p) => (renewalProbabilityMap[p.renewalprobabilityid] = p))

  renewalProbabilityStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(renewalprobabilityid).then(() =>
  Client.del(`renewalprobability?renewalprobabilityid=eq.${renewalprobabilityid}`)
    .then((renewalProbability: RenewalProbability) => {
      delete renewalProbabilityMap[renewalProbability.renewalprobabilityid]
      push_undo({
        undofunc: () => {
          Client.post('renewalprobability', renewalProbability)
          renewalProbabilityMap[renewalProbability.renewalprobabilityid] = renewalProbability
          renewalProbabilityStream.next({ id, state: 'ok', value: currentValue })
        },
        redofunc: () => deleteOne(propertyid, renewalprobabilityid)
      })
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      renewalProbabilityStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchRenewalProbability(propertyid)
      return err
    })
  )
}

function patch(renewalProbability: RenewalProbability): Promise<RenewalProbability | { message: string }> {
  const currentValue: RenewalProbability[] = renewalProbabilityStream.getValue().value
  const renewalprobabilityid: number = renewalProbability.renewalprobabilityid
  const currentRenewalProbability = _.find(currentValue, { renewalprobabilityid })
  const changeset = diff_obj(renewalProbability, currentRenewalProbability)
  if (_.isEmpty(changeset)) return new Promise(() => currentValue)
  push_undo({
    undofunc: () => patch(currentRenewalProbability),
    redofunc: () => patch(renewalProbability)
  })

  // optimistic update
  renewalProbabilityStream.next({
    id,
    state: 'ok',
    value: _.set(_.cloneDeep(currentValue), _.findIndex(currentValue, {renewalprobabilityid}), renewalProbability)
  })

  return _patches_.watch(renewalprobabilityid, Client.patch(`renewalprobability?renewalprobabilityid=eq.${renewalprobabilityid}`, changeset)
    .then((mf: RenewalProbability[]) => {
      _.forEach(mf, (renewalProbability) => (renewalProbabilityMap[renewalProbability.renewalprobabilityid] = renewalProbability))
      return mf[0]
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      renewalProbabilityStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      const propertyid = _.get(currentRenewalProbability, 'propertyid')
      if (propertyid) fetchRenewalProbability(propertyid)
      return err
    }))

}

// public module interface //

const renewalProbabilityService = {
  stream: () => renewalProbabilityStream.asObservable(),
  value: () => renewalProbabilityStream.getValue().value,
  eventStream: () => renewalProbabilityEventStream.asObservable(),
  clear,
  copy,
  create,
  copies,
  deleteOne,
  get,
  fetchRenewalProbability,
  patch,
  refresh
}

export default renewalProbabilityService
